import { DEFAULT_VEHICLE_COLOR, type CreateTrailerInput } from '@chukua/common';
import { VehicleColorField } from '../../../components/VehicleColorField';
import { Alert, Button, Drawer, Form, Input, InputNumber, Select, Switch } from 'antd';
import { useTrailerTypeOptions } from '../hooks/use-trailer-type-options';
import { CAPACITY_TONNES_RULES } from '../../../../../shared/policies/capacity-tonnes-rules';

interface CreateTrailerDrawerProps {
  open: boolean;
  error: string | null;
  submitting: boolean;
  onCreate: (input: CreateTrailerInput) => Promise<boolean>;
  onClose: () => void;
}

/** Registration, type, capacity, colour and whether it carries a genset; the type says what it is and its axles (gitaumoses4/chukua#13, #390). */
export function CreateTrailerDrawer({ open, error, submitting, onCreate, onClose }: CreateTrailerDrawerProps) {
  const [form] = Form.useForm<CreateTrailerInput>();
  const typeOptions = useTrailerTypeOptions();

  async function submit(values: CreateTrailerInput) {
    const created = await onCreate(values);
    if (created) {
      form.resetFields();
      onClose();
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Create trailer" placement="right" size={420} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<CreateTrailerInput>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={submit}
        disabled={submitting}
        initialValues={{ hasGenset: false, color: DEFAULT_VEHICLE_COLOR }}
      >
        <Form.Item name="registration" label="Registration" rules={[{ required: true, message: 'Enter a registration' }]}>
          <Input className="font-mono" autoFocus />
        </Form.Item>
        <Form.Item name="typeCode" label="Trailer type" rules={[{ required: true, message: 'Select a trailer type' }]}>
          <Select
            options={typeOptions.map((type) => ({ value: type.code, label: type.name }))}
            placeholder="Select a trailer type"
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item name="capacityTonnes" label="Capacity (tonnes)" rules={CAPACITY_TONNES_RULES}>
          <InputNumber className="w-full" min={0.01} />
        </Form.Item>
        <VehicleColorField />
        <Form.Item
          name="hasGenset"
          label="Genset for a reefer container"
          valuePropName="checked"
          extra="An underslung or clip-on set on this trailer. What the trailer is, and its axles, come from its type."
        >
          <Switch />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Create trailer
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
